import { Button, Icon } from '@blueprintjs/core';
import { useObserver } from 'mobx-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  DataSyncItem,
  Destination,
  MappingMode,
  OptimizelyProduct,
  Source,
  SyncStrategy,
  Target
} from '../../../types/DataSyncItem';
import { DataSyncViewModel } from '../../../types/DataSyncViewModel';
import { DataSyncViewModelConverter } from '../../../utils/dataSyncViewModelConverter';
import styles from './DataSyncForm.module.sass';
import {
  FieldMappingsTableSkeleton,
  TreeFieldMappingsTable,
  useFieldMappingHeaderInfo,
} from './FormSections/TreeFieldMappingsTable';
import DataSyncFormSource from './FormSections/DataSyncFormSource';
import DataSyncFormDestination from './FormSections/DataSyncFormDestination';
import { IconNames } from '@blueprintjs/icons';
import DataSyncScheduleSection from './FormSections/DataSyncScheduleSection';
import DataSyncConfigSection from './FormSections/DataSyncConfigSection';
import MainContainer from '../MainContainer';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { DataSyncMappingService } from '../../../services/DataSync/DataSyncMappingService';
import { useFieldMappingMode } from '../../../hooks/useFieldMappingMode';
import { useDataSyncSave } from '../../../hooks/useDataSyncSave';
import { useGetStrategy } from '../../../hooks/useDataSyncStrategy';
import { useDataSyncObjectStore } from '../../../hooks/useDataSyncStore';
import { useFetchStores } from '../../../hooks/useFetchStore';
import { FieldDefinition } from '../../../types/FieldDefinition';
import { resetDestinationFieldMappings, resetSourceFieldMappings, findFieldByPath } from '../../../lib/dataSync';
import { useValidation } from '../../../hooks/useDataSyncFormValidator';
import { findBestMatch } from '../../../lib/fieldMatcher';
import { getInvalidSourceFields, getFieldMappingValidations } from '../../../utils/fieldMappingValidator';
import { FieldMappingEntry } from '../../../types/DataSyncViewModel';
import { ValidationType } from '../../../types/FieldMapping';

/**
 * Filters out system fields that should not be shown in the field mappings UI
 * Currently filters out: _metadata
 */
const filterSystemFields = (fields: FieldDefinition[]): FieldDefinition[] => {
  return fields.filter((field) => field.name !== '_metadata');
};

export interface DataSyncCreateProps {
  mode: 'create' | 'edit';
  onSubmit: (syncObj: DataSyncItem) => Promise<void>;
  onCancel: () => void;
  initialValues: DataSyncItem;
}

const DataSyncForm = ({mode, onSubmit, onCancel, initialValues}: DataSyncCreateProps) => {
  const isFirstLoad = useRef(true);
  const [dataSyncViewModel, setViewModel] = useState<DataSyncViewModel>(
    DataSyncViewModelConverter.fromBackend(initialValues)
  );

  const [backendDataSync] = useState<DataSyncItem>(initialValues);
  const getStrategy = useGetStrategy(dataSyncViewModel);

  const sourceStore = useDataSyncObjectStore(Target.SOURCE, dataSyncViewModel?.source);
  const destinationStore = useDataSyncObjectStore(Target.DESTINATION, dataSyncViewModel?.destination);

  const {loaded: storesLoaded} = useFetchStores(sourceStore, destinationStore);

  const {
    refs: { configRef, sourceRef, destinationRef, mappingsRef, scheduleRef },
    isValid,
  } = useValidation(dataSyncViewModel, getStrategy);

  const showScheduleSection = () => {
    return getStrategy() === SyncStrategy.SCHEDULED;
  };

  const [fieldMappingMode, setFieldMappingMode] = useFieldMappingMode(
    mode, backendDataSync, sourceStore, destinationStore, storesLoaded
  );

  const [destinationFields, setDestinationFields] = useState<FieldDefinition[]>([]);
  const [sourceFields, setSourceFields] = useState<FieldDefinition[]>([]);

  // Calculate source and destination header info for field mapping table
  const { sourceInfo, destinationInfo } = useFieldMappingHeaderInfo(
    dataSyncViewModel.source,
    dataSyncViewModel.destination,
    sourceStore,
    destinationStore,
    storesLoaded
  );

  const { handleSave, loading, loadingMessage } = useDataSyncSave(
    initialValues,
    dataSyncViewModel,
    destinationStore,
    onSubmit,
    isValid,
    sourceFields,
    destinationFields
  );

  useEffect(() => {
    if (
      isFirstLoad.current &&
      !dataSyncViewModel.isNew &&
      fieldMappingMode &&
      fieldMappingMode === MappingMode.FIXED &&
      storesLoaded &&
      sourceFields.length > 0 &&
      destinationFields.length > 0
    ) {
      DataSyncMappingService.generateMappingsForInheritedSchema(
        dataSyncViewModel.source,
        dataSyncViewModel.destination,
        sourceStore,
        destinationStore,
        dataSyncViewModel.fieldMappings
      ).then(({ mappings: generatedMappings, updatedFields }) => {
        const sortedMappings = generatedMappings.sort((a, b) =>
          a.sourceField.localeCompare(b.sourceField)
        );
        setViewModel((prev) => ({ ...prev, fieldMappings: sortedMappings }));
        // set the destination fields in case there were new fields added to the destination object
        // for disabled mappings which are not there in the CG Content Type
        setDestinationFields(filterSystemFields(updatedFields));
      });
      isFirstLoad.current = false;
    }
  }, [
    fieldMappingMode,
    dataSyncViewModel,
    storesLoaded,
    sourceFields,
    destinationFields,
  ]);

  useEffect(() => {
    if (storesLoaded && destinationStore) {
      destinationStore.getDestinationObjectFields(dataSyncViewModel.destination.object.object_name)
        .then((fields) => setDestinationFields(filterSystemFields(fields)))
        .catch(() => setDestinationFields([]));
    } else {
      setDestinationFields([]);
    }
  }, [storesLoaded, destinationStore, dataSyncViewModel.destination.object.object_name]);

  useEffect(() => {
    if (storesLoaded && sourceStore) {
      sourceStore.getSourceObjectFields(dataSyncViewModel.source.object.object_name)
        .then((fields) => setSourceFields(filterSystemFields(fields)))
        .catch(() => setSourceFields([]));
    } else {
      setSourceFields([]);
    }
  }, [storesLoaded, sourceStore, dataSyncViewModel.source.object.object_name]);

  const actionButtons = (
    <>
      <Button text="Cancel" onClick={onCancel} />
      <Button
        text="Save"
        intent="primary"
        onClick={handleSave}
        disabled={loading}
      />
    </>
  );

  const inheritSourceSchema = async (newDestination: Destination, baseType?: string) => {
    const updatedFieldMappings = await DataSyncMappingService.inheritSourceSchema(
      dataSyncViewModel.source,
      newDestination.object.object_name,
      sourceStore,
      destinationStore,
      baseType
    );

    const destinationObject = destinationStore?.getTransientDestinationObject();
    if (destinationObject?.fields) {
      setDestinationFields(filterSystemFields(destinationObject.fields));
    }

    const sortedMappings = updatedFieldMappings.sort((a, b) =>
      a.sourceField.localeCompare(b.sourceField)
    );
    setViewModel((prev) => ({
      ...prev,
      destination: newDestination,
      fieldMappings: sortedMappings
    }));
    setFieldMappingMode(MappingMode.FIXED);
  };

  const resetFixedMappingMode = () => {
    if (fieldMappingMode === MappingMode.FIXED) {
      destinationStore?.setTransientDestinationObject(undefined);
      setFieldMappingMode(MappingMode.EDITABLE);
      setViewModel((prev) => ({ ...prev, fieldMappings: [] }));
    }
  };

  const onDestinationChange = (newDestination: Destination) => {
    const currentDestinationObjectName = dataSyncViewModel.destination.object.object_name;
    const hasFieldMappings = dataSyncViewModel.fieldMappings.length > 0;
    const isNewDestination = currentDestinationObjectName !== newDestination.object.object_name;

    // Reset destination fields to INCOMPLETE if destination object has changed and we have existing mappings
    if (
      fieldMappingMode !== MappingMode.FIXED &&
      isNewDestination &&
      hasFieldMappings
    ) {
      const fieldMappings = resetDestinationFieldMappings(
        dataSyncViewModel.fieldMappings
      );
      setViewModel((prev) => ({
        ...prev,
        destination: newDestination,
        fieldMappings,
      }));
    } else {
      setViewModel((prev) => ({ ...prev, destination: newDestination }));
    }

    if (destinationStore?.getTransientDestinationObject()?.object_name === newDestination?.object.object_name) {
      return;
    }

    DataSyncMappingService.isCGDestinationInheritedFromSource(
      dataSyncViewModel.source, newDestination, sourceStore, destinationStore)
      .then(async (isInherited) => {
        if (isInherited) {
          await inheritSourceSchema(newDestination);
        } else if (fieldMappingMode === MappingMode.FIXED) {
          resetFixedMappingMode();
        }
      });
  };

  const onSourceChange = (newSource: Source) => {
    const currentSourceObjectName = dataSyncViewModel.source.object.object_name;
    const hasFieldMappings = dataSyncViewModel.fieldMappings.length > 0;

    // Reset source fields to INCOMPLETE if source object has changed and we have existing mappings
    if (currentSourceObjectName !== newSource.object.object_name && hasFieldMappings) {
      const fieldMappings = resetSourceFieldMappings(dataSyncViewModel.fieldMappings);
      setViewModel((prev) => ({ ...prev, source: newSource, fieldMappings }));
    } else {
      setViewModel((prev) => ({ ...prev, source: newSource }));
    }

    if (fieldMappingMode === MappingMode.FIXED) {
      resetFixedMappingMode();
    } else {
      DataSyncMappingService.isCGDestinationInheritedFromSource(
        newSource, dataSyncViewModel.destination, sourceStore, destinationStore)
        .then(async (isInherited) => {
          if (isInherited) {
            await inheritSourceSchema(dataSyncViewModel.destination);
          }
        });
    }
  };

  /**
   * Auto-map handler that iteratively finds best matching source fields for unmapped destinations
   * Works one field at a time to ensure each mapping affects validation of subsequent mappings
   * Updates the view model directly instead of returning mappings for single render cycle
   */
  const handleAutoMap = useCallback((): void => {
    // Helper to collect all leaf nodes from tree structure with their full paths
    const collectLeafDestinations = (fields: FieldDefinition[], prefix = ''):
    Array<{field: FieldDefinition, path: string}> => {
      const leaves: Array<{field: FieldDefinition, path: string}> = [];

      for (const field of fields) {
        const fieldPath = prefix ? `${prefix}/${field.name}` : field.name;

        if (field.object_schema?.fields) {
          // Object type with nested fields
          leaves.push(...collectLeafDestinations(field.object_schema.fields, fieldPath));
        } else if (field.array && field.array.type === 'object' && 'fields' in field.array) {
          // Array of objects with nested fields
          leaves.push(...collectLeafDestinations(field.array.fields, fieldPath));
        } else {
          // Leaf field (primitive or array of primitives)
          leaves.push({ field, path: fieldPath });
        }
      }

      return leaves;
    };

    // Get all leaf destination fields
    const allLeafDestinations = collectLeafDestinations(destinationFields);

    // Start with existing mappings
    let currentMappings = [...dataSyncViewModel.fieldMappings];

    // Filter to only unmapped destination fields
    const unmappedDestinations = allLeafDestinations.filter((item) => {
      const existingMapping = currentMappings.find(
        (m) => m.destinationField === item.path
      );
      return !existingMapping || !existingMapping.sourceField;
    });

    if (unmappedDestinations.length === 0) {
      return;
    }

    // Process each unmapped destination field iteratively
    for (const destItem of unmappedDestinations) {
      const destField = destItem.field;
      const destPath = destItem.path;

      // This ensures each mapping affects validation of subsequent mappings
      const invalidSourceFields = getInvalidSourceFields(
        destPath,
        sourceFields,
        destinationFields,
        currentMappings // Use current state to reflect previous iterations
      );
      const excludePaths = new Set<string>(invalidSourceFields.keys());

      // Use field matcher to find best matches (already sorted by score, highest first)
      const bestMatches = findBestMatch(destField, sourceFields, excludePaths);

      if (bestMatches.length === 0) {
        continue;
      }

      // Use the top suggested match (bestMatches are already filtered and sorted by score)
      const sourceField = bestMatches[0];
      const sourcePath = sourceField.name;

      // Validate the mapping before applying it (double-check even though already excluded invalid fields)
      const validation = getFieldMappingValidations({
        source: sourcePath,
        destination: destPath,
        sourceFields,
        destinationFields,
      });

      // Only accept if there's no validation ERROR (warnings are acceptable)
      if (!validation || validation.type !== ValidationType.ERROR) {
        // Check if this source is already used in current mappings
        const existingMappingWithSource = currentMappings.find(
          (m) => m.sourceField === sourcePath && !m.disabled
        );

        if (existingMappingWithSource) {
          // Disable the previous mapping that used this source
          currentMappings = currentMappings.map((m) =>
            m.destinationField === existingMappingWithSource.destinationField
              ? { ...m, disabled: true }
              : m
          );
        }

        // Check if there's an existing mapping for this destination (with empty sourceField)
        const existingDestMapping = currentMappings.find(
          (m) => m.destinationField === destPath
        );

        if (existingDestMapping) {
          // Update the existing mapping
          currentMappings = currentMappings.map((m) =>
            m.destinationField === destPath
              ? { ...m, sourceField: sourcePath, disabled: false, isAutoMapped: true }
              : m
          );
        } else {
          // Add a new mapping
          currentMappings.push({
            destinationField: destPath,
            sourceField: sourcePath,
            disabled: false,
            isAutoMapped: true,
          });
        }
      }
    }

    // Update view model directly for single render cycle
    setViewModel((prev) => ({ ...prev, fieldMappings: currentMappings }));
  }, [destinationFields, sourceFields, dataSyncViewModel.fieldMappings]);

  return useObserver(() => (
    <MainContainer
      title="To create a new sync, define a source and destination. Then setup field mapping"
      headerActions={actionButtons}
      scrollableContent={true}
    >
      <div className={styles.container}>
        <LoadingOverlay isOpen={loading} message={loadingMessage} />
        <DataSyncConfigSection
          value={{ name: dataSyncViewModel.name }}
          ref={configRef}
          onChange={(obj) => {
            setViewModel((prev) => ({ ...prev, name: obj.name }));
          }}
        />

        <div className={styles.row}>
          <DataSyncFormSource
            initialValue={dataSyncViewModel.source}
            ref={sourceRef}
            dataSyncObjectsStore={sourceStore}
            onChange={onSourceChange}
            mode={mode}
          />
          <div className={styles.arrowRight}>
            <Icon icon={IconNames.ARROW_RIGHT} />
          </div>

          <DataSyncFormDestination
            initialValue={dataSyncViewModel.destination}
            ref={destinationRef}
            dataSyncObjectsStore={destinationStore}
            onChange={onDestinationChange}
            inheritSourceSchema={inheritSourceSchema}
            source={dataSyncViewModel.source}
          />
        </div>

        <div className={styles.row}>
          {fieldMappingMode === undefined ||
          (mode === 'edit' &&
            sourceFields.length === 0 &&
            destinationFields.length === 0) ? (
            <FieldMappingsTableSkeleton />
          ) : (
            <TreeFieldMappingsTable
              ref={mappingsRef}
              viewModelFieldMappings={dataSyncViewModel.fieldMappings}
              sourceFields={sourceFields}
              destinationFields={destinationFields}
              fieldMappingMode={fieldMappingMode}
              sourceInfo={sourceInfo}
              destinationInfo={destinationInfo}
              onAutoMap={handleAutoMap}
              onChange={(newFieldMappings) => {
                setViewModel((prev) => ({
                  ...prev,
                  fieldMappings: newFieldMappings,
                }));
              }}
            />
          )}
        </div>

        {showScheduleSection() && (
          <div className={styles.row}>
            <DataSyncScheduleSection
              value={dataSyncViewModel.interval}
              ref={scheduleRef}
              onChange={(value: string) => {
                setViewModel((prev) => ({ ...prev, interval: value }));
              }}
            />
          </div>
        )}
      </div>
    </MainContainer>
  ));
};

export default DataSyncForm;
